/**
 * Which statements are overdue. A "series" is the run of statements covering
 * one set of accounts (a card, or a bank's combined statement for six
 * accounts), so a combined statement raises one alert, not six. Each series
 * learns its own cadence from its history; with one statement it is assumed
 * monthly. See ARCHITECTURE.md, Overdue statements.
 */
import type { Row } from './google/sheets'
import { accountKey } from './filing'

export { accountKey }

const STATEMENT_TYPES = new Set(['bank_statement', 'credit_card_statement', 'brokerage_statement', 'retirement_statement', 'loan_statement'])
const COUNTED = new Set(['done', 'review'])

/** Assumed gap when a series has only one statement. */
const DEFAULT_CADENCE_DAYS = 31
/** Statements post a few days after the period ends; allow a week before nagging. */
const GRACE_DAYS = 7

export interface Overdue {
  /** Stable id for the series: its sorted account keys (see accountKey). */
  key: string
  /** What to ask for, e.g. "Fidelity Rewards Visa Signature statement". */
  name: string
  institution: string
  last_end: string
  /** When the next statement's period should have ended. */
  expected_end: string
  days_since: number
  cadence_days: number
  /** More than one statement is missing. */
  missed: number
  /** The accounts as filed on the latest statement, for marking them closed. */
  account_ids: string[]
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim())
const DAY = 86_400_000
const toDays = (date: string) => Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10)) / DAY
const fromDays = (d: number) => new Date(d * DAY).toISOString().slice(0, 10)

const accountSet = (ids: string) => [
  ...new Set(
    ids
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(accountKey),
  ),
].sort()

/** "2026-10-16 Fidelity Rewards Visa Signature statement.pdf" → "Fidelity Rewards Visa Signature statement". */
function seriesName(doc: Row): string {
  const name = str(doc.name).replace(/\.[A-Za-z0-9]{1,5}$/, '').replace(/^\d{4}-\d{2}-\d{2}\s+/, '')
  return name || `${str(doc.institution)} statement`
}

export function overdueStatements(documents: Row[], accounts: Row[], today: string): Overdue[] {
  const closed = new Set(accounts.filter((a) => str(a.closed)).map((a) => accountKey(str(a.account_id))))
  const docs = documents.filter(
    (d) => COUNTED.has(str(d.status)) && STATEMENT_TYPES.has(str(d.doc_type)) && /^\d{4}-\d{2}-\d{2}$/.test(str(d.period_end)) && str(d.account_id),
  )

  // Latest statement end seen for each account, across every series.
  const latestForAccount = new Map<string, string>()
  for (const d of docs) {
    for (const id of accountSet(str(d.account_id))) {
      if (str(d.period_end) > (latestForAccount.get(id) ?? '')) latestForAccount.set(id, str(d.period_end))
    }
  }

  const series = new Map<string, Row[]>()
  for (const d of docs) {
    const key = accountSet(str(d.account_id)).join(',')
    series.set(key, [...(series.get(key) ?? []), d])
  }

  const out: Overdue[] = []
  for (const [key, list] of series) {
    const ids = key.split(',')
    if (ids.every((id) => closed.has(id))) continue
    const ends = [...new Set(list.map((d) => str(d.period_end)))].sort()
    const last = ends.at(-1)!
    // Every account here also appears on a newer statement elsewhere: this
    // series was replaced (say, separate statements merged into a combined one).
    if (ids.every((id) => (latestForAccount.get(id) ?? '') > last)) continue

    const gaps = ends.slice(1).map((e, i) => toDays(e) - toDays(ends[i])).filter((g) => g > 0).sort((a, b) => a - b)
    const cadence = gaps.length ? gaps[Math.floor(gaps.length / 2)] : DEFAULT_CADENCE_DAYS
    const expected = toDays(last) + cadence
    const now = toDays(today)
    if (now < expected + GRACE_DAYS) continue

    const latest = list.find((d) => str(d.period_end) === last)!
    out.push({
      key,
      name: seriesName(latest),
      institution: str(latest.institution),
      last_end: last,
      expected_end: fromDays(expected),
      days_since: now - toDays(last),
      cadence_days: cadence,
      missed: Math.max(1, Math.floor((now - GRACE_DAYS - toDays(last)) / cadence)),
      account_ids: str(latest.account_id).split(',').map((s) => s.trim()).filter(Boolean),
    })
  }
  return out.sort((a, b) => b.days_since - a.days_since)
}
